import { CalendarEventDetail } from '@webcat/commons-web-components/dist/calendar/models/calendar-event-detail';
import Moment from 'moment';
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Ref, Watch } from 'vue-property-decorator';
import { Validations } from 'vuelidate-property-decorators';

@Component({})
class EdDatePickerComponent extends Vue {

  /* In */
  @Prop() readonly label: string;
  @Prop() readonly disabled: boolean;
  @Prop() readonly placeholder: string;
  @Prop() readonly format: string;
  @Prop() readonly allowedDates: string[];
  @Prop() readonly workingDays: boolean[];
  @Prop() readonly events: any[];
  @Prop() readonly eventsColor: Function;
  @Prop() readonly current: string;
  @Prop() readonly disclamer: string;
  @Prop() readonly disclamerColor: string;
  @Prop({ default: '' }) readonly minDate: string;
  @Prop({ default: null }) parentElement: HTMLElement | null;
  @Prop({ default: true }) readonly closeOnDateChange: boolean;

  /* Out */
  public readonly onChange: string = 'onChange';
  public readonly onMouseOverDate: string = 'onMouseOverDate';
  public readonly onMouseLeaveDate: string = 'onMouseLeaveDate';

  /* Data */
  public value: string = '';
  public selectedDate: string = '';
  public currentDate: string = '';
  public isCalendarVisible: boolean = false; 
  public calendarPosition: { top: string | 'auto'; bottom: string | 'auto'; left: string } = { top: '100%', bottom: 'auto', left: '0px' };
  public DEFAULT_CALENDAR_MODE = 'month';

  @Ref('wcCalendar') readonly wcCalendar: any;
  @Ref('dateInput')readonly  dateInput!: HTMLInputElement | null;
  @Ref('calendarContainer')readonly calendarContainer!: HTMLElement | null; 

  get disclamerStyle() {
    return {
      color: `var(--ed-color-${this.disclamerColor || 'light-contrast'})`
    }
  }

  get date() {
    let format = this.format || 'MM/DD/YYYY';
    return this.selectedDate ? Moment(this.selectedDate).format(format) : '';
  }

  get isPlaceholderShown() {
    return this.selectedDate ? false : true;
  }

  get required() {
    let attrs: any = this.$attrs;
    if (attrs) {
      if (attrs.value) return attrs.value.validators.required ? true : false;
      return false;
    }
    return false;
  }

  get datePickerClass() {
    return {
      'ed-date-picker--disabled': this.disabled
    }
  }

  get uniqueEvents(): string[] {
    if (!this.events) {
      return [];
    }

    const uniqueDates = new Set<string>();
    return this.events.filter((dateString: string) => {
      if (uniqueDates.has(dateString)) {
        return false; 
      }
      uniqueDates.add(dateString);
      return true; 
    });
  }

  get mappedEvents(): CalendarEventDetail[] {
    if (!this.uniqueEvents) {
      return [];
    }

    return this.uniqueEvents
    .map((dateString: string) => {

      return {
        date: this.dateFormatted(dateString),
        color: this.doEventsColor(dateString),
      };
    })
    .filter((event) => event.color !== undefined)
    .map((event) => {
      const eventDetail: CalendarEventDetail = {
        color: event.color,
        allDay: true,
        summary: '',
        endDate: event.date,
        startDate: event.date,
        wcRendered: false,
        wcRenderLevel: 0,
        wcRenderLevelNextWeek: 0,
      };

      return eventDetail;
    });
  }

  /* Methods */
  created() {
    this.setValue();
    this.setCurrentDate();
    this.updateValue();
  }

  updated() {
    this.setCurrentDate();
  }

  beforeDestroy() { 
    document.removeEventListener('click', this.onClickOutside.bind(this));
    (this.wcCalendar as HTMLElement)?.removeEventListener('is-date-allowed', this.handleIsDateAllowed.bind(this));
  }

  setCalendarEvents() {
    if (this.wcCalendar?.setEvents) {
      this.wcCalendar.setEvents(this.mappedEvents);
    }
  }

  dateFormatted(date:string):Date{
    const arrayDate = date?.split('-');
    if(arrayDate && arrayDate.length >= 2) {
      return new Date(parseInt(arrayDate[0]),parseInt(arrayDate[1])-1,parseInt(arrayDate[2]));
    }
    return null;
  }

  doEventsColor(value) {
    if(this.eventsColor) {
      return this.eventsColor(value);
    }
  }

  handleIsDateAllowed(event) {
    const date = event.detail.value.date
    event.detail.value.allowed = this.canSelectDate(date);
  }

  setCurrentDate() {
    if (this.current) {
      this.currentDate = this.current;
    } else {
      this.currentDate = this.allowedDates ? this.allowedDates[0] : '';
    }
  }
    
  setValue() {
    let attrs: any = this.$attrs;
    let value = attrs.value ? attrs.value.value : '';
    this.value = value;
    this.selectedDate = value;
  }

  updateValue() {
    let attrs: any = this.$attrs;
    let validators = attrs?.value?.validators || {};
    this.$emit('input', {
      value: this.selectedDate ? Moment(this.selectedDate).format('YYYY-MM-DD') : '',
      valid: !this.$v?.value?.$invalid,
      validators
    });
  }

  onChangeDate(date) {
    this.$emit(this.onChange, date);
    this.updateValue();
  }

  canSelectDate(date: string) {
    let allowed = this.allowedDates ? this.allowedDates.includes(date) : true;
    if (this.workingDays && this.workingDays.length === 7) {
      allowed = this.workingDays.at(Moment(date).day());
    }

    return allowed;
  }

  cleanDate() {
    if (!this.disabled) {
      this.value = '';
      this.selectedDate = '';
      this.onChangeDate(this.selectedDate);
    }
  }

  openCalendar() {
    if (!this.disabled) {
      this.value = this.selectedDate;
      if (!this.selectedDate) {
        this.value = this.currentDate;
      }
      this.isCalendarVisible = true;
      this.$nextTick(() => {
        document.addEventListener('click', this.onClickOutside.bind(this));
        (this.wcCalendar as HTMLElement).addEventListener('is-date-allowed', this.handleIsDateAllowed.bind(this));
        this.setCalendarEvents();
      });
    }
  }

  adjustCalendarPosition() {
    if (!this.dateInput ) {
      return;
    }
    let parentElement = this.$parent.$el;
    if(this.parentElement) parentElement = this.parentElement;
    
    const parentRect = parentElement.getBoundingClientRect();
    const inputRect = this.dateInput.getBoundingClientRect();
    const spaceBelow = parentRect.bottom - inputRect.bottom;
    const spaceRight = parentRect.right - inputRect.right;
    const calendarWidth = 370;
    const calendarHeight = 400;

  
    const newPosition: { top: string | 'auto'; bottom: string | 'auto'; left: string; right: string | 'auto'} = {
      top: '100%',
      bottom: 'auto',
      left: '0px',
      right: 'auto'
    };

    if (spaceBelow < calendarHeight) {
      newPosition.top = 'auto';
      newPosition.bottom = `${30-spaceBelow}px`;
    } 

     if (spaceRight < calendarWidth) { 
       newPosition.left = 'auto';
      newPosition.right = `${10-spaceRight}px`;
     }

    this.calendarPosition = newPosition; 
  }
  
  onClickOk() {
    this.selectedDate = this.value;
    this.onChangeDate(this.selectedDate); 
    this.isCalendarVisible = false;
  }

  onCalendarChange(e) {
    this.value = e.detail.value; 
    this.selectedDate = this.value;
    this.onChangeDate(this.selectedDate);
    if (this.closeOnDateChange) {
      this.isCalendarVisible = false;
      this.closeContextMenu();
    }
  }

  onClickOutside(event) {
    this.$nextTick(() => {
      const target = event.target;
      const calendarContainer = this.$el.querySelector('.calendar-container');
  
      if (this.isCalendarVisible && calendarContainer && !calendarContainer.contains(target) && !this.$el.contains(target)) {
        this.isCalendarVisible = false;
        this.value = '';
      }
    });
  }

  onMouseOverOnDate(e) {
    const {date, mouseEvent} = e?.detail?.value;
    if(this.canSelectDate(date)) {
      if (this.wcCalendar instanceof HTMLElement && this.wcCalendar.shadowRoot) {
          const monthDaysElement = this.wcCalendar.shadowRoot.querySelector('wc-calendar-month-days');
          if (monthDaysElement instanceof HTMLElement && monthDaysElement.shadowRoot) {
              const el = monthDaysElement.shadowRoot.getElementById(date);
              if (el) {
                this.$emit(this.onMouseOverDate, {date, position: { x: mouseEvent.x, y: mouseEvent.y }});
              }
          } 
      } 
    }
  }

  onMouseLeaveOnDate(e) {
    this.closeContextMenu();
  }

  closeContextMenu(): void {
    this.$emit(this.onMouseLeaveDate);
  }

  @Validations()
  validations() {
    let attrs: any = this.$attrs;
    return {
      value: attrs.value ? attrs.value.validators : {}
    };
  }

  @Watch('$attrs')
  onAttrsChanged() {
    setTimeout(() => {
      this.setValue();
    }, 5);
    
  }

  @Watch('isCalendarVisible')
  onIsCalendarVisibleChanged(newValue: boolean) {
    if(newValue == true){
      this.$nextTick(() => {    
        this.adjustCalendarPosition();
      });
    }
  }
}

export default Vue.component('ed-date-picker', EdDatePickerComponent);